import { VariantProps, cva } from 'class-variance-authority';
import { twMerge } from 'tailwind-merge';

const SIZE_LARGE = 'large';
const SIZE_MEDIUM = 'medium';
const SIZE_SMALL = 'small';
const SIZE_EXTRA_LARGE = 'extra-large';
const TAG_H1 = 'h1';
const TAG_H2 = 'h2';
const TAG_H3 = 'h3';
const TAG_DIV = 'div';

function getDefaultTag(size: string): string {
  const defaultSizeToTagMap: { [key: string]: string } = {
    [SIZE_LARGE]: TAG_H1,
    [SIZE_MEDIUM]: TAG_H2,
    [SIZE_SMALL]: TAG_H3,
    [SIZE_EXTRA_LARGE]: TAG_DIV
  };
  return defaultSizeToTagMap[size] || TAG_H1;
}

export interface HeadingProps extends VariantProps<typeof headingVariants> {
  size?: 'small' | 'medium' | 'large' | 'extra-large';
  text?: string;
  descriptionTop?: string;
  descriptionBottom?: string;
  hasHorizontalRule?: boolean;
  hasMarginBottom?: boolean;
  className?: string;
  headingTag?: 'h1' | 'h2' | 'h3' | 'div';
  id?: string;
}

const wrapperVariants = cva('preflight !ds-font-sans', {
  variants: {
    size: {
      [SIZE_SMALL]: 'ds-mb-3',
      [SIZE_MEDIUM]: 'ds-mb-5',
      [SIZE_LARGE]: 'ds-mb-5',
      [SIZE_EXTRA_LARGE]: 'ds-mb-6'
    }
  }
});

const headingVariants = cva('ds-text-neutrals-darker !ds-font-sans', {
  variants: {
    size: {
      [SIZE_SMALL]: 'ds-text-xl ds-font-semibold',
      [SIZE_MEDIUM]: 'ds-text-2xl ds-font-semibold',
      [SIZE_LARGE]: 'ds-text-3xl ds-font-bold',
      [SIZE_EXTRA_LARGE]: 'ds-text-4xl ds-font-semibold'
    }
  }
});

const descriptionTopVariants = cva('ds-mt-0 ds-font-bold ds-text-neutrals-dark', {
  variants: {
    size: {
      [SIZE_SMALL]: 'ds-mb-1 ds-text-sm',
      [SIZE_MEDIUM]: 'ds-mb-2 ds-text-sm',
      [SIZE_LARGE]: 'ds-mb-2 ds-text-base',
      [SIZE_EXTRA_LARGE]: 'ds-mb-3 ds-text-base'
    }
  }
});

const descriptionBottomVariants = cva('ds-mb-0 ds-text-neutrals-dark', {
  variants: {
    size: {
      [SIZE_SMALL]: 'ds-mt-1 ds-text-base',
      [SIZE_MEDIUM]: 'ds-mt-2 ds-text-base',
      [SIZE_LARGE]: 'ds-mt-2 ds-text-base',
      [SIZE_EXTRA_LARGE]: 'ds-mt-3 ds-text-base'
    }
  }
});

const horizontalRuleVariants = cva('ds-text-neutrals-light', {
  variants: {
    size: {
      [SIZE_SMALL]: 'ds-mt-2',
      [SIZE_MEDIUM]: 'ds-mt-2',
      [SIZE_LARGE]: 'ds-mt-2',
      [SIZE_EXTRA_LARGE]: 'ds-mt-3'
    }
  }
});

const Heading = ({
  size = SIZE_LARGE,
  className = '',
  text = '',
  descriptionTop = '',
  descriptionBottom = '',
  hasHorizontalRule = false,
  hasMarginBottom = true,
  headingTag,
  ...props
}: HeadingProps) => {
  const getTopDescription = descriptionTop ? (
    <p className={descriptionTopVariants({ size })}>{descriptionTop}</p>
  ) : null;
  const getBottomDescription = descriptionBottom ? (
    <p className={descriptionBottomVariants({ size })}>{descriptionBottom}</p>
  ) : null;
  const getHorizontalRule = hasHorizontalRule ? (
    <hr className={horizontalRuleVariants({ size })} />
  ) : null;
  const getWrapperClass = hasMarginBottom ? wrapperVariants({ size }) : 'preflight';

  const headings: { [key: string]: JSX.Element } = {
    div: <div className={headingVariants({ size })}>{text}</div>,
    h3: <h3 className={headingVariants({ size })}>{text}</h3>,
    h2: <h2 className={headingVariants({ size })}>{text}</h2>,
    h1: <h1 className={headingVariants({ size })}>{text}</h1>
  };

  return (
    <div className={twMerge(getWrapperClass, className)} {...props}>
      {getTopDescription}
      {headings[headingTag || getDefaultTag(size)]}
      {getBottomDescription}
      {getHorizontalRule}
    </div>
  );
};

export default Heading;
